import { RiInformation2Line, RiLoader4Line, RiRefreshLine } from "@remixicon/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

interface BranchRow {
	number: number;
	name: string;
	isActive: boolean;
	firstSeenAt: string;
	lastSeenAt: string;
}

interface SyncJob {
	id: string;
	jobType: string;
	status: string;
	itemsProcessed?: number;
	itemsFailed?: number;
	details?: {
		phase?: string;
		processed?: number;
		failed?: number;
	} | null;
}

const QUERY_KEY = ["prohandel", "branches"] as const;
const SYNC_JOBS_LATEST_KEY = ["prohandel", "sync-jobs", "latest"] as const;
const SYNC_STATUS_KEY = ["prohandel", "sync-status"] as const;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

async function fetchLatestSyncJob(): Promise<SyncJob | null> {
	const { data, error } = await fetchClient.GET(
		"/api/v1/prohandel/sync/jobs" as never,
		{ params: { query: { limit: 1 } } } as never,
	);
	if (error) throw new Error("failed to load sync jobs");
	const list = (data as { data?: SyncJob[] } | undefined)?.data ?? [];
	return list[0] ?? null;
}

function formatRelative(iso: string): string {
	const ms = Date.now() - new Date(iso).getTime();
	if (ms < 60_000) return "just now";
	const minutes = Math.floor(ms / 60_000);
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
	const days = Math.floor(hours / 24);
	return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function BranchTable() {
	const qc = useQueryClient();

	type BranchChange = { branchNumber: number; previouslyActive: boolean };
	const [pendingChanges, setPendingChanges] = useState<BranchChange[]>([]);
	const lastSyncStarted = useRef<string | null>(null);

	const query = useQuery({
		queryKey: QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/prohandel/branches" as never);
			if (error) throw new Error("failed to load branches");
			return data as BranchRow[];
		},
	});

	const toggle = useMutation({
		mutationFn: async (vars: { number: number; isActive: boolean }) => {
			const { error } = await fetchClient.PATCH(
				"/api/v1/prohandel/branches/{number}" as never,
				{
					params: { path: { number: vars.number } },
					body: { isActive: vars.isActive },
				} as never,
			);
			if (error) throw new Error("failed to toggle branch");
		},
		onMutate: async (vars) => {
			await qc.cancelQueries({ queryKey: QUERY_KEY });
			const prev = qc.getQueryData<BranchRow[]>(QUERY_KEY);
			qc.setQueryData<BranchRow[]>(QUERY_KEY, (old) =>
				old?.map((b) => (b.number === vars.number ? { ...b, isActive: vars.isActive } : b)),
			);
			return { prev };
		},
		onSuccess: (_data, vars, ctx) => {
			const prevRow = ctx?.prev?.find((b) => b.number === vars.number);
			setPendingChanges((s) => {
				if (s.some((c) => c.branchNumber === vars.number)) return s;
				return [
					...s,
					{
						branchNumber: vars.number,
						previouslyActive: prevRow?.isActive ?? false,
					},
				];
			});
		},
		onError: (_err, _vars, ctx) => {
			if (ctx?.prev) qc.setQueryData(QUERY_KEY, ctx.prev);
			toast.error("Could not toggle branch");
		},
		onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
	});

	const refresh = useMutation({
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST("/api/v1/prohandel/branches/refresh" as never);
			if (error) throw new Error("refresh failed");
			return data as BranchRow[];
		},
		onSuccess: (data) => {
			const prev = qc.getQueryData<BranchRow[]>(QUERY_KEY) ?? [];
			const newCount = data.filter((b) => !prev.some((p) => p.number === b.number)).length;
			qc.setQueryData(QUERY_KEY, data);
			toast.success(
				newCount > 0 ? `Branch list refreshed (${newCount} new)` : "Branch list refreshed",
			);
		},
		onError: () => toast.error("Could not refresh branches. Save credentials first."),
	});

	const triggerFastInStock = useMutation({
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST(
				"/api/v1/prohandel/sync" as never,
				{ params: { query: { mode: "fast_in_stock" } } } as never,
			);
			if (error) throw new Error("failed to start sync");
			return data as { message?: string };
		},
		onSuccess: async () => {
			toast.success("fast_in_stock sync started");
			await qc.invalidateQueries({ queryKey: SYNC_JOBS_LATEST_KEY });
			const refetched = await qc.fetchQuery({
				queryKey: SYNC_JOBS_LATEST_KEY,
				queryFn: fetchLatestSyncJob,
			});
			lastSyncStarted.current = refetched?.id ?? null;
		},
		onError: () => toast.error("Could not start sync"),
	});

	const refreshSizes = useMutation({
		mutationFn: async (branchNumbers: number[]) => {
			const { data, error } = await fetchClient.POST(
				"/api/v1/prohandel/branches/refresh-sizes" as never,
				{ body: { branchNumbers } } as never,
			);
			if (error) throw new Error("refresh-sizes failed");
			return data as { syncJobId: string; message: string };
		},
		onSuccess: async (data) => {
			toast.success(data.message);
			await qc.invalidateQueries({ queryKey: SYNC_JOBS_LATEST_KEY });
			const refetched = await qc.fetchQuery({
				queryKey: SYNC_JOBS_LATEST_KEY,
				queryFn: fetchLatestSyncJob,
			});
			lastSyncStarted.current = refetched?.id ?? null;
		},
		onError: () => toast.error("Could not start branch refresh"),
	});

	const syncStatusQuery = useQuery({
		queryKey: SYNC_STATUS_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/prohandel/sync/status" as never);
			if (error) throw new Error("failed to load sync status");
			return data as Array<{
				resourceName: string;
				lastChangeId: number;
				lastSyncedAt: string | null;
			}>;
		},
	});

	const isColdStart = (() => {
		const articleState = syncStatusQuery.data?.find((s) => s.resourceName === "article");
		if (!articleState) return true;
		return articleState.lastChangeId === 0;
	})();

	const latestJobQuery = useQuery({
		queryKey: SYNC_JOBS_LATEST_KEY,
		queryFn: fetchLatestSyncJob,
		// Poll whenever the banner is visible: pending changes OR a sync
		// is currently running. Stops polling completely when there is
		// nothing to watch.
		refetchInterval: (q) => {
			const job = q.state.data;
			const running = job?.status === "running" || job?.status === "queued";
			if (running || pendingChanges.length > 0) {
				return 5_000;
			}
			return false;
		},
	});

	const syncInProgress =
		latestJobQuery.data?.status === "running" || latestJobQuery.data?.status === "queued";

	useEffect(() => {
		const job = latestJobQuery.data;
		if (
			job &&
			lastSyncStarted.current &&
			job.id === lastSyncStarted.current &&
			(job.status === "completed" || job.status === "failed")
		) {
			setPendingChanges([]);
			lastSyncStarted.current = null;
		}
	}, [latestJobQuery.data]);

	const activeCount = query.data?.filter((b) => b.isActive).length ?? 0;
	const totalCount = query.data?.length ?? 0;
	const sorted = query.data ? [...query.data].sort((a, b) => a.number - b.number) : [];
	const added = pendingChanges.filter((c) => !c.previouslyActive).length;
	const removed = pendingChanges.filter((c) => c.previouslyActive).length;
	const pendingCount = pendingChanges.length;

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium">Branches to sync stock from</p>
					{query.data && query.data.length > 0 ? (
						<p className="text-xs text-muted-foreground">
							{activeCount} of {totalCount} active. Inactive branches don't contribute stock.
						</p>
					) : null}
				</div>
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => refresh.mutate()}
					disabled={refresh.isPending}
				>
					{refresh.isPending ? (
						<RiLoader4Line className="size-3.5 mr-1.5 animate-spin" />
					) : (
						<RiRefreshLine className="size-3.5 mr-1.5" />
					)}
					Refresh
				</Button>
			</div>

			{(pendingCount > 0 || syncInProgress) && (
				<output className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 flex items-center justify-between gap-3">
					<div className="flex items-center gap-2 min-w-0">
						{syncInProgress ? (
							<RiLoader4Line className="size-4 text-amber-600 animate-spin shrink-0" />
						) : (
							<RiInformation2Line className="size-4 text-amber-600 shrink-0" />
						)}
						<span className="text-sm truncate">
							{syncInProgress ? (
								<>
									Sync in progress
									{latestJobQuery.data?.details?.phase
										? ` (${latestJobQuery.data.details.phase})`
										: ""}
									{typeof latestJobQuery.data?.itemsProcessed === "number"
										? ` - ${latestJobQuery.data.itemsProcessed.toLocaleString()} processed`
										: ""}
								</>
							) : added === 0 && removed > 0 ? (
								<>
									{removed} branch{removed === 1 ? "" : "es"} removed. Data purged. No re-sync
									needed.
								</>
							) : isColdStart ? (
								<>Full sync required (~40 min) - articles have never been synced.</>
							) : (
								<>
									{added > 0 && (
										<>
											{added} branch{added === 1 ? "" : "es"} added
											{removed > 0 && `, ${removed} removed (data purged)`}.{" "}
										</>
									)}
									Refresh added branches (~few min) or run a full sync.
								</>
							)}
						</span>
					</div>
					{!syncInProgress && (
						<div className="flex gap-2 shrink-0">
							{added > 0 && !isColdStart && (
								<Button
									size="sm"
									onClick={() =>
										refreshSizes.mutate(
											pendingChanges.filter((c) => !c.previouslyActive).map((c) => c.branchNumber),
										)
									}
									disabled={refreshSizes.isPending}
								>
									{refreshSizes.isPending
										? "Starting..."
										: `Refresh ${added} branch${added === 1 ? "" : "es"}`}
								</Button>
							)}
							{added > 0 && (
								<Button
									size="sm"
									variant={isColdStart ? "default" : "outline"}
									onClick={() => triggerFastInStock.mutate()}
									disabled={triggerFastInStock.isPending}
								>
									{triggerFastInStock.isPending ? "Starting..." : "Run Fast Sync"}
								</Button>
							)}
						</div>
					)}
				</output>
			)}

			{query.isLoading ? (
				<div data-testid="branch-table-skeleton" className="space-y-2">
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
				</div>
			) : query.error ? (
				<div
					role="alert"
					className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
				>
					<p className="font-medium">Could not load branches.</p>
					<Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
						Retry
					</Button>
				</div>
			) : sorted.length === 0 ? (
				<div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
					<p className="font-medium text-foreground mb-1">No branches discovered yet</p>
					<p>Save your credentials and run a sync, or click Refresh.</p>
				</div>
			) : (
				<div className="border rounded-md overflow-hidden">
					<Table className="table-fixed">
						<TableHeader>
							<TableRow>
								<TableHead className="w-12">#</TableHead>
								<TableHead>Name</TableHead>
								<TableHead className="w-28">Last seen</TableHead>
								<TableHead className="w-16 text-right">Active</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sorted.map((b) => {
								const firstSeen = new Date(b.firstSeenAt);
								const isNew =
									!b.isActive && Date.now() - firstSeen.getTime() < TWENTY_FOUR_HOURS_MS;
								return (
									<TableRow key={b.number}>
										<TableCell className="font-mono text-xs text-muted-foreground">
											{b.number}
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2 min-w-0">
												<span className="truncate">{b.name || "(no name)"}</span>
												{isNew ? <Badge variant="secondary">new</Badge> : null}
											</div>
										</TableCell>
										<TableCell className="text-xs text-muted-foreground">
											{formatRelative(b.lastSeenAt)}
										</TableCell>
										<TableCell className="text-right">
											<Switch
												checked={b.isActive}
												aria-label={`Toggle branch ${b.number}`}
												onCheckedChange={(checked) =>
													toggle.mutate({
														number: b.number,
														isActive: checked,
													})
												}
											/>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			)}

			<p className="text-xs text-muted-foreground">
				The fast in-stock sync sums stock per SKU across active branches.
			</p>
		</div>
	);
}
